import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

/**
 * Request notification permission from the user
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    throw new Error('Denne browser understøtter ikke notifikationer');
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
};

/**
 * Register service worker for push notifications
 */
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Workers understøttes ikke i denne browser');
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
    });

    console.log('Service Worker registreret:', registration);

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;

    return registration;
  } catch (error) {
    console.error('Service Worker registrering fejlede:', error);
    throw error;
  }
};

/**
 * Get VAPID public key from server
 */
export const getVapidPublicKey = async (): Promise<string> => {
  const token = localStorage.getItem('token');
  const response = await axios.get(`${API_BASE_URL}/notifications/vapid-public-key`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data.publicKey;
};

/**
 * Convert base64 VAPID key to Uint8Array
 */
const urlBase64ToUint8Array = (base64String: string): Uint8Array<ArrayBuffer> => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray as Uint8Array<ArrayBuffer>;
};

/**
 * Subscribe to push notifications
 */
export const subscribeToPushNotifications = async (): Promise<PushSubscription> => {
  try {
    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Get VAPID public key
    const vapidPublicKey = await getVapidPublicKey();

    // Subscribe to push notifications
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    // Send subscription to server
    const token = localStorage.getItem('token');
    await axios.post(
      `${API_BASE_URL}/notifications/subscribe`,
      {
        subscription: subscription.toJSON(),
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    console.log('Push subscription oprettet:', subscription);
    return subscription;
  } catch (error) {
    console.error('Push subscription fejlede:', error);
    throw error;
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPushNotifications = async (): Promise<void> => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Unsubscribe from browser
      await subscription.unsubscribe();

      // Remove subscription from server
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/notifications/unsubscribe`,
        {
          endpoint: subscription.endpoint,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('Push subscription fjernet');
    }
  } catch (error) {
    console.error('Unsubscribe fejlede:', error);
    throw error;
  }
};

/**
 * Check if user is subscribed to push notifications
 */
export const isSubscribedToPush = async (): Promise<boolean> => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (error) {
    console.error('Kunne ikke tjekke push subscription:', error);
    return false;
  }
};

/**
 * Sync the current browser push subscription with the server.
 * Called silently on app startup so the server always has an up-to-date
 * subscription even if it was deleted due to an earlier expiry (410).
 * Does nothing if the browser has no subscription or permission is not granted.
 */
export const syncPushSubscriptionWithServer = async (): Promise<void> => {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (Notification.permission !== 'granted') return;

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    await axios.post(
      `${API_BASE_URL}/notifications/subscribe`,
      { subscription: subscription.toJSON() },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );
    console.log('Push subscription synkroniseret med server');
  } catch (error) {
    // Silent — don't disrupt app startup
    console.warn('Push subscription sync fejlede:', error);
  }
};

/**
 * Initialize push notifications for a user
 * Requests permission, registers service worker, and subscribes to push.
 * If a subscription already exists in the browser it is re-synced with the
 * server (the server may have deleted it after receiving a 410 expiry error).
 */
export const initializePushNotifications = async (): Promise<void> => {
  try {
    // Request permission
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.log('Notification permission ikke givet');
      return;
    }

    // Register service worker
    await registerServiceWorker();

    // Check if browser already has a subscription
    const registration = await navigator.serviceWorker.ready;
    const existingSubscription = await registration.pushManager.getSubscription();

    if (existingSubscription) {
      // Re-sync with server: the server may have deleted this subscription
      // when a previous delivery attempt returned a 410 (expired/invalid).
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/notifications/subscribe`,
        { subscription: existingSubscription.toJSON() },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      console.log('Eksisterende push subscription re-synkroniseret med server');
      return;
    }

    // No existing subscription — create a new one
    await subscribeToPushNotifications();

    console.log('Push notifications initialiseret');
  } catch (error) {
    console.error('Kunne ikke initialisere push notifications:', error);
    throw error;
  }
};

export interface NotificationPrefs {
  inAppLike: boolean;
  inAppComment: boolean;
  inAppCoComment: boolean;
  inAppCommentLike: boolean;
  inAppEventInvite: boolean;
  inAppEventReminder: boolean;
  inAppEventAttendeeComment: boolean;
  inAppMessage: boolean;
  inAppNewPost: boolean;
  pushLike: boolean;
  pushComment: boolean;
  pushCoComment: boolean;
  pushCommentLike: boolean;
  pushEventInvite: boolean;
  pushEventReminder: boolean;
  pushEventAttendeeComment: boolean;
  pushMessage: boolean;
  pushNewPost: boolean;
  emailLike: boolean;
  emailComment: boolean;
  emailCoComment: boolean;
  emailCommentLike: boolean;
  emailEventInvite: boolean;
  emailEventReminder: boolean;
  emailEventAttendeeComment: boolean;
  emailMessage: boolean;
  emailNewPost: boolean;
}

export const getNotificationPreferences = async (): Promise<NotificationPrefs> => {
  const token = localStorage.getItem('token');
  const response = await axios.get(`${API_BASE_URL}/notifications/preferences`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const updateNotificationPreferences = async (
  prefs: Partial<NotificationPrefs>
): Promise<NotificationPrefs> => {
  const token = localStorage.getItem('token');
  const response = await axios.patch(`${API_BASE_URL}/notifications/preferences`, prefs, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};
